/**
 * Welcome — first-run front door at `/welcome` (guest-first onboarding).
 *
 * Get Started → tutorial (→ guest mode, no sign-up wall).
 * I already have an account → sign-in.
 *
 * A user who already has a session (real or guest) is redirected into the app —
 * the Welcome screen is only for people without a session.
 * See specs/guest-first-onboarding/spec.md (Story 1).
 */

import { Redirect, useRouter, useLocalSearchParams, type Href } from 'expo-router';
import { WelcomeScreen } from '../components/onboarding/WelcomeScreen/WelcomeScreen';
import { ErrorBoundary } from '../components/layout';
import { useSupabaseAuth } from '../src/shared/contexts/SupabaseAuthContext';
import { resolveFirstRunRoute } from '../src/onboarding/helpers/resolveFirstRunRoute.helper';
import { buildPublicSignInHref } from '../src/signIn/helpers/postAuthRedirect.helper';

function WelcomeRouteContent() {
  const router = useRouter();
  const { isLoaded, isSignedIn, isAnonymous } = useSupabaseAuth();
  // Preserve a deep-link returnUrl (set when AuthGuard bounced an unauthenticated user here) so
  // "I already have an account" lands the user back where they were headed after sign-in.
  const { returnUrl } = useLocalSearchParams<{ returnUrl?: string }>();

  if (!isLoaded) return null;

  // Anyone with a live session (real user or existing guest) skips Welcome.
  if (resolveFirstRunRoute({ isSignedIn, isAnonymous }) === 'app') {
    return <Redirect href={'/app/' as Href} />;
  }

  return (
    <WelcomeScreen
      onGetStarted={() => router.replace('/app/tutorial' as Href)}
      onHaveAccount={() => router.replace(buildPublicSignInHref({ returnUrl }) as Href)}
    />
  );
}

export default function WelcomeRoute() {
  return (
    <ErrorBoundary>
      <WelcomeRouteContent />
    </ErrorBoundary>
  );
}
